import { useState } from 'react';
import { useGame } from '../state/SocketProvider';
import { rememberSettings } from '../state/settingsPrefsStore';
import { CARD_CATALOG, resolveCost, type CardDeck } from '../../shared/cards';
import { SEEKER_COST } from '../../shared/hiderdeck';

/** Host Card Settings (§5): every card in both decks with description, an on/off
 *  toggle, and a count stepper. Count = hider copies / seeker plays-per-round.
 *  Changes go through set_settings (server-validated). Behind a disclosure. */
export function CardSettings() {
  const { state, send } = useGame();
  const [open, setOpen] = useState(false);
  if (!state) return null;
  const cfg = state.settings.deckConfig ?? {};

  // v29 BOLT-165 (Jake): "Since there's limited horizontal space, potentially
  // the list of seeker cards should have a tab/control of some sort that
  // switches between viewing and editing the # of cards vs the cost of the
  // cards." One stepper, two things it can be pointed at.
  const [editing, setEditing] = useState<'count' | 'cost'>('count');

  const setCard = (id: string, patch: { enabled?: boolean; count?: number; cost?: number }) => {
    const cat = CARD_CATALOG.find((c) => c.id === id)!;
    const cur = cfg[id] ?? { enabled: true, count: cat.defaultCount };
    // v29 BOLT-142: remembered for the next game. Jake's own example is a card
    // count - "change the number of radius cards from the default to let's say
    // six… a week later create a new game, it should have that same radius six".
    const settings = { deckConfig: { [id]: { ...cur, ...patch } } };
    rememberSettings(settings);
    send({ t: 'set_settings', settings });
  };

  // v29 BOLT-165 (Jake): "I think we need a short description for the setting
  // sections". A stepper labelled "plays" tells you what it counts, not what it
  // DOES; these say what changing it will do to the game.
  const groups: { deck: CardDeck; title: string; unit: string; blurb: string }[] = [
    {
      deck: 'seeker',
      title: 'Seeker questions',
      unit: editing === 'cost' ? 'cost' : 'plays',
      blurb:
        editing === 'cost'
          ? 'How many cards the hider is dealt when the seeker plays this. The price of asking.'
          : 'How many times the seeker may play each card in a round.',
    },
    {
      deck: 'hider',
      title: 'Hider deck',
      unit: 'copies',
      blurb: 'How many of each card go into the hider deck. It reshuffles when it runs out, so these are proportions, not a limit.',
    },
  ];

  return (
    <div className="panel section-gap card-settings" data-testid="card-settings">
      <button className="gs-disclose" data-testid="cs-toggle-panel" onClick={() => setOpen((o) => !o)}>
        {open ? '▾ Card settings' : '▸ Card settings'}
      </button>
      {open &&
        groups.map((g) => (
          <div key={g.deck}>
            <div className="cs-group">
              {g.title} <span className="cs-unit">({g.unit})</span>
            </div>
            <div className="cs-blurb" data-testid={`cs-blurb-${g.deck}`}>
              {g.blurb}
            </div>
            {/* The seeker list is the only one with two numbers worth editing,
                so it is the only one that gets the switch. */}
            {g.deck === 'seeker' && (
              <div className="cs-tabs" data-testid="cs-tabs" role="tablist">
                {(['count', 'cost'] as const).map((mode) => (
                  <button
                    key={mode}
                    className={`cs-tab${editing === mode ? ' cs-tab--on' : ''}`}
                    role="tab"
                    aria-selected={editing === mode}
                    data-testid={`cs-tab-${mode}`}
                    onClick={() => setEditing(mode)}
                  >
                    {mode === 'count' ? 'Plays' : 'Cost'}
                  </button>
                ))}
              </div>
            )}
            {CARD_CATALOG.filter((c) => c.deck === g.deck).map((c) => {
              const conf = cfg[c.id];
              const enabled = conf?.enabled !== false;
              const count = conf?.count ?? c.defaultCount;
              // v29 BOLT-165: the stepper edits whichever number the tab is
              // pointed at. Cost is seeker-only - a hider card is not played
              // FOR anything, so it has no price.
              const costing = editing === 'cost' && g.deck === 'seeker';
              const cost = resolveCost(cfg, c.id, SEEKER_COST[c.id] ?? 1);
              const shown = costing ? cost : count;
              const bump = (next: number) =>
                costing ? setCard(c.id, { enabled: true, cost: Math.max(1, next) }) : setCard(c.id, { enabled: true, count: Math.max(0, next) });
              return (
                <div className="cs-row" data-testid={`cs-${c.id}`} data-enabled={enabled} key={c.id}>
                  <button
                    className="cs-onoff"
                    data-on={enabled}
                    data-testid={`cs-toggle-${c.id}`}
                    onClick={() => setCard(c.id, { enabled: !enabled })}
                  >
                    {enabled ? 'On' : 'Off'}
                  </button>
                  <div className="cs-info">
                    <div className="cs-name">{c.label}</div>
                    <div className="cs-desc">{c.description}</div>
                  </div>
                  <div className="cs-stepper">
                    <button className="gs-step" data-testid={`cs-${c.id}-minus`} onClick={() => bump(shown - 1)}>
                      −
                    </button>
                    <span className="cs-count" data-testid={`cs-${c.id}-count`} data-editing={costing ? 'cost' : 'count'}>
                      {enabled || costing ? shown : 0}
                    </span>
                    <button className="gs-step" data-testid={`cs-${c.id}-plus`} onClick={() => bump(shown + 1)}>
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
}
